import { useState, useEffect } from 'react';
import allStore, { createStore } from 'store';

import { STORE_NAMESPACE } from '../components/global/DismissiblePersistent';

/**
 * Dismissible persistent hook that remembers whether something was
 * dismissed by storing it in localStorage.
 *
 * @param {string} storeKey A unique key to store state in localStorage.
 * @param {Object?} customStore useful when we want to use another type of storage (i.e, sessionStorage)..
 * @returns {{onDismiss: Function, visible: boolean}}
 */
const useDismissiblePersistent = (storeKey, customStore) => {
  // Start the component always hidden, since its
  // visibility state depends on client-side data
  // (i.e. localStorage, etc). Its actual visibility
  // is determined in an effect
  const [visible, setVisible] = useState(false);
  const store = customStore ? createStore(customStore) : allStore;

  const isDismissed = () => {
    const { dismissedKeys = [] } = store.get(STORE_NAMESPACE) || {};
    return dismissedKeys.includes(storeKey);
  };

  const onDismiss = () => {
    setVisible(false);
    const localStore = store.get(STORE_NAMESPACE) || {};
    const { dismissedKeys = [] } = localStore;

    store.set(STORE_NAMESPACE, {
      ...localStore,
      dismissedKeys: [...dismissedKeys, storeKey],
    });
  };

  useEffect(() => {
    if (!isDismissed()) {
      setVisible(true);
    }
  }, []);

  return {
    visible,
    onDismiss,
  };
};

export default useDismissiblePersistent;
